/**
 * The one API client (AD-14).
 *
 * Every failure arrives as `{ error: { code, message, field? } }`; `ApiError`
 * carries that envelope through so a screen can show the message the API wrote
 * rather than inventing its own wording. Field names stay `snake_case` end to
 * end — nothing here renames them.
 *
 * Story 1.4 replaces the hand-written response types below with types generated
 * from the OpenAPI schema.
 *
 * Story 2.3, judgement call 2: `apiFetch` is the CSRF-aware wrapper —
 * Epic 1's own requirement (`app/api/security.py`'s `verify_csrf`, AD-14
 * "mutating routes require CSRF"), never exercised by the frontend until this
 * story because nothing mutated before. There is deliberately no second
 * "mutate" wrapper: `apiFetch` is the one call site every screen already uses
 * for every request, GET included, so attaching the header here — rather
 * than asking each caller to remember it — is what
 * `tests/architecture/csrf-header-guard.test.ts` enforces by scanning for a
 * raw `fetch(` anywhere else under `frontend/src`.
 */

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    field?: string;
    // Story 2.3 review round 2: the raw value a `conflict` refusal is
    // naming as the row's current state, alongside `field` — present only
    // on that one shape, so a caller (e.g. `UsersList`'s conflict handling)
    // can relabel it through its own plain-language map without scraping a
    // value back out of `message`'s prose.
    current_value?: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field: string | undefined;
  readonly currentValue: string | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.error.code;
    this.field = body.error.field;
    this.currentValue = body.error.current_value;
  }
}

const GENERIC_FAILURE = "Something went wrong. Please try again in a moment.";

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null || !("error" in value)) {
    return false;
  }
  const error: unknown = (value as { error: unknown }).error;
  return (
    typeof error === "object" &&
    error !== null &&
    typeof (error as { code?: unknown }).code === "string" &&
    typeof (error as { message?: unknown }).message === "string"
  );
}

export function toApiError(status: number, payload: unknown): ApiError {
  if (isApiErrorBody(payload)) {
    return new ApiError(status, payload);
  }
  return new ApiError(status, { error: { code: "unexpected_response", message: GENERIC_FAILURE } });
}

//: Methods `verify_csrf` (`app/api/security.py`) never checks — matching its
//: own `SAFE_METHODS`, so this file cannot drift into sending (or, worse,
//: relying on the server to ignore) a header the backend never asked for.
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);

//: `app/api/security.py`'s `CSRF_HEADER` — the exact spelling the backend's
//: `verify_csrf` reads.
const CSRF_HEADER = "X-PropDesk-CSRF";

//: `app/api/security.py`'s `cookie_name`/`HOST_PREFIX`: `propdesk_csrf` in
//: development, `__Host-propdesk_csrf` in production. Checked in this order
//: — `__Host-` preferred — so a browser that somehow carries both (a stale
//: dev cookie left over from a mixed environment, say) doesn't read the
//: weaker one. Exact names, not a suffix match (review finding: a suffix
//: match also accepts an unrelated cookie like `x-propdesk_csrf` or
//: `my_propdesk_csrf`, which is not the token the backend actually issued).
const CSRF_COOKIE_NAMES = ["__Host-propdesk_csrf", "propdesk_csrf"];

//: The exact refusal `app/api/security.py`'s `verify_csrf` gives a request
//: with no CSRF cookie/header pair — reused verbatim so a locally-refused
//: mutation (below) reads identically to one the server itself refused.
const CSRF_UNCONFIRMED_MESSAGE = "Your session could not be confirmed. Please reload the page and try again.";

/**
 * The CSRF cookie's value, or `undefined` if neither known name is set (an
 * unauthenticated request has none).
 */
function readCsrfToken(): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  const cookies = new Map<string, string>();
  for (const entry of document.cookie.split(";")) {
    const separator = entry.indexOf("=");
    if (separator === -1) {
      continue;
    }
    const name = entry.slice(0, separator).trim();
    cookies.set(name, decodeURIComponent(entry.slice(separator + 1)));
  }
  for (const name of CSRF_COOKIE_NAMES) {
    const value = cookies.get(name);
    if (value) {
      return value;
    }
  }
  return undefined;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = { Accept: "application/json", ...(init?.headers as Record<string, string> | undefined) };

  if (!SAFE_METHODS.has(method)) {
    const token = readCsrfToken();
    if (!token) {
      // Refused locally rather than sent without the header and left for
      // the server to reject (review finding): the server would refuse it
      // identically, but only after a real round trip, and only after
      // whatever it did in between the request landing and CSRF being
      // checked — refusing here is strictly earlier and strictly cheaper,
      // with the exact same message the server's own `verify_csrf` gives.
      throw new ApiError(403, { error: { code: "csrf_missing", message: CSRF_UNCONFIRMED_MESSAGE } });
    }
    headers[CSRF_HEADER] = token;
  }

  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw toApiError(response.status, payload);
  }
  return payload as T;
}

export interface Health {
  status: string;
  version: string;
  app_env: string;
  checked_at: string;
}

export function fetchHealth(): Promise<Health> {
  return apiFetch<Health>("/health");
}
